import React, { useState, useEffect } from 'react';
import { Typography, Tag, Space, Button, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useChat } from '../../contexts/ChatContext';
import { NewThreadDialog } from '../NewThreadDialog';
import { ModelType } from '../../types/models';
import { ThreadModelService } from '../../services/api/threadModelService';
import { PrimeArtifact } from '../../types/primeModel';
import { ArtifactTree } from '../../types/artifacts';

const { Text } = Typography;

export const ThreadContext: React.FC = () => {
  const { threads, currentThreadId, updateThread, deleteThread } = useChat();
  const [isEditing, setIsEditing] = useState(false);
  const [primeArtifacts, setPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [companyTrees, setCompanyTrees] = useState<ArtifactTree[]>([]);
  
  const currentThread = threads.find(thread => thread.id === currentThreadId);
  
  // Load model options for display
  useEffect(() => {
    if (currentThread) {
      loadModelOptions();
    }
  }, [currentThread]);

  const loadModelOptions = async () => {
    try {
      const { primeRoots, companyTrees: trees } = await ThreadModelService.getAllModelOptions();
      setPrimeArtifacts(primeRoots);
      setCompanyTrees(trees);
    } catch (error) {
      console.error('Failed to load model options for display:', error);
    }
  };

  const getModelDisplayName = () => {
    if (!currentThread || !currentThread.model) return 'Unknown';
    
    const { model } = currentThread;
    if (model.type === ModelType.PRIME && model.artifactId) {
      const artifact = primeArtifacts.find(a => a.id === model.artifactId);
      return artifact ? `${artifact.name}` : `Prime (${model.artifactId})`;
    } else if (model.type === ModelType.COMPANY && model.artifactTreeId) {
      const tree = companyTrees.find(t => t.id === model.artifactTreeId);
      return tree ? `${tree.name}` : `Company (${model.artifactTreeId})`;
    }
    
    return model.type === ModelType.PRIME ? 'Prime' : 'Company';
  };
  
  if (!currentThread) {
    return null;
  }

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async (threadData: { name: string; model: any; uiRole: string; description?: string }) => {
    try {
      await updateThread(currentThread.id, {
        name: threadData.name,
        model: threadData.model,
        uiRole: threadData.uiRole,
        description: threadData.description,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update thread:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDelete = async (threadId: string) => {
    if (deleteThread) {
      await deleteThread(threadId);
      setIsEditing(false);
    }
  };
  
  return (
    <>
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ color: 'white', fontSize: '14px' }}>
              {currentThread.name}
            </Text>
            <Space size="small">
              <Tag color="blue" style={{ fontSize: '11px' }}>
                {getModelDisplayName()}
              </Tag>
              <Tag color="green" style={{ fontSize: '11px' }}>
                {currentThread.uiRole}
              </Tag>
              <Tooltip title="Edit thread properties">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  style={{ color: 'white', fontSize: '12px' }}
                />
              </Tooltip>
            </Space>
          </div>
          
          {currentThread.context && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {currentThread.context.selectedArtifactId && (
                <Tag color="purple" style={{ fontSize: '10px' }}>
                  Artifact: {currentThread.context.selectedArtifactId}
                </Tag>
              )}
              {currentThread.context.selectedTaskId && (
                <Tag color="orange" style={{ fontSize: '10px' }}>
                  Task: {currentThread.context.selectedTaskId}
                </Tag>
              )}
              {currentThread.context.environment && (
                <Tag color="cyan" style={{ fontSize: '10px' }}>
                  Env: {currentThread.context.environment}
                </Tag>
              )}
            </div>
          )}
          
          <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px' }}>
            {currentThread.messageCount} messages • Updated {new Date(currentThread.updatedAt).toLocaleDateString()}
          </Text>
        </Space>
      </div>

      <NewThreadDialog
        visible={isEditing}
        onCancel={handleCancel}
        onOk={handleSave}
        onDelete={handleDelete}
        initialData={{
          id: currentThread.id,
          name: currentThread.name,
          model: currentThread.model,
          uiRole: currentThread.uiRole,
          description: currentThread.description,
          messageCount: currentThread.messageCount,
        }}
      />
    </>
  );
};
